import React, { useEffect } from 'react';
import styled from 'styled-components';
import * as color from '@constant/color';
import * as size from '@constant/size';
import TextBlock from '@component/common/block/text-block';
import { actions as modalActions } from '@store/slice/modal-slice';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { usePlaySound } from '@hook/common/useSound';
import { ITodayStudyModalData } from '@datatype/iyagigo/type';
import { actions as navigationActions } from '@store/slice/navigation-slice';
import { createClozePath } from '@constant/iyagigo/route-path';
import { useNavigate } from 'react-router-dom';
import { useTodayClozeWorkbookQuery } from '@hook/iyagigo/useApi';
import { getClozeTypeBySubject } from '@util/workbook';
import Gap from '@component/common/gap';
import { getIsTodayCreated, getIsTodayStudied } from '@util/workbook';
import { EApiLanguageCode } from '@datatype/iyagigo/api-type';
import { useWorkbookSync } from '@hook/iyagigo/useStudent';
import TextButton from '@design-system/text-button';
import { ETheme } from '@datatype/type';

interface IProps {
  data: ITodayStudyModalData['data'];
}

// ############### type ###############
const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  width: ${size.MEDIUM_MODAL_WIDTH};
`;

const ContentContainer = styled.div`
  width: 100%;
`;

const TestMetadataContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  background-color: ${color.BROWN_4};
  padding: ${size.MARGIN_1};
  margin: ${size.MARGIN_1} 0px ${size.MARGIN_3} 0px;
  gap: ${size.MARGIN_1};
`;

const ProblemCountContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  width: 100%;
`;

const TodayStudyModal: React.FC<IProps> = ({ data }) => {
  // ############### route ###############
  const navigate = useNavigate();

  // ############### state ###############
  const dispatch = useTypedDispatch();
  const studyLanguage = useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? null;
  const isAuthenticated = useTypedSelector((state) => state.rootReducer?.userReducer?.isAuthenticated) ?? false;
  const playSound = usePlaySound();

  // ############### api ###############
  const workbookQuery = useTodayClozeWorkbookQuery(
    getClozeTypeBySubject(data.study),
    studyLanguage === EApiLanguageCode.ENGLISH ? EApiLanguageCode.ENGLISH : EApiLanguageCode.JAPANESE,
  );
  const { reviewWorkbook, newWorkbook } = useWorkbookSync(workbookQuery.data?.data || []);

  // ############### handler ###############
  const handleBeginButtonClick = () => {
    // playSound('tap');
    dispatch(modalActions.hideModal());
    dispatch(navigationActions.storeIyagigoSubMenu({ mainMenu: 'cloze', subMenu: data.study }));
    navigate(createClozePath('study'));
  };

  // ############### variable ###############
  let label: string;

  switch (data.study) {
    case 'vocabulary':
      label = '어휘';
      break;
    case 'grammar':
      label = '문법';
      break;
    default:
      label = '학습';
      break;
  }

  if (!workbookQuery.data?.data || workbookQuery.data?.data.length === 0) return <div></div>;

  return (
    <Wrapper>
      <ContentContainer>
        <TextBlock
          text={`오늘의 ${label}`}
          design={{
            text: { size: 'medium', color: color.BLACK_2, bold: true },
            box: { alignX: 'left' },
          }}
        />
        <TestMetadataContainer>
          <ProblemCountContainer>
            <TextBlock
              text={`새로운 ${label}`}
              design={{
                text: { size: 'medium', color: color.BLACK_2, bold: false },
                box: { alignX: 'left' },
              }}
            />
            <TextBlock
              text={`${newWorkbook?.length}문제`}
              design={{
                text: { size: 'medium', color: color.BLACK_2, bold: false },
                box: { alignX: 'left' },
              }}
            />
          </ProblemCountContainer>
          <ProblemCountContainer>
            <TextBlock
              text={`복습할 ${label}`}
              design={{
                text: { size: 'medium', color: color.BLACK_2, bold: false },
                box: { alignX: 'left' },
              }}
            />
            <TextBlock
              text={`${reviewWorkbook?.length}문제`}
              design={{
                text: { size: 'medium', color: color.BLACK_2, bold: false },
                box: { alignX: 'left' },
              }}
            />
          </ProblemCountContainer>
        </TestMetadataContainer>
        <TextButton text='시작하기' theme={ETheme.BROWN} size='medium' shape='pill' width='100%' handleClick={handleBeginButtonClick} />
      </ContentContainer>
    </Wrapper>
  );
};

export default TodayStudyModal;
